import { inject, Injectable } from "@angular/core";
import { MonitoringCategory, MonitoringProductService } from "@domain/product/monitoring/monitoring-product.service";
import { ProductImage } from "@domain/product/product-image";
import { UtilityConfiguration } from "@domain/project/configurations/monitoring/utility-configuration";
import { MonitoringProductItem } from "@project/monitoring-product-item";
import { catchError, map, Observable, of, shareReplay, startWith, switchMap } from "rxjs";

@Injectable({ providedIn: "root" })
export class UtilityProductService {
  private readonly monitoringProductService = inject(MonitoringProductService);
  private readonly imageUrlCache = new Map<UtilityConfiguration, Observable<string>>();

  resolveName(config: UtilityConfiguration, country?: string): Observable<string> {
    const quantities = MonitoringProductItem.buildDeviceQuantityMap(config.getOptions());
    if (quantities.size !== 1) return of(config.name);

    const matNr = Array.from(quantities.keys())[0];
    return this.monitoringProductService.init().pipe(
      switchMap(() => this.monitoringProductService.loadDeviceFromDB(matNr, MonitoringCategory.UTILITY, country)),
      map((device: any) => device?.name || config.name),
      startWith(config.name),
    );
  }

  resolveImageUrl(config: UtilityConfiguration): Observable<string> {
    const cached = this.imageUrlCache.get(config);
    if (cached) return cached;

    const groupUrl = ProductImage.getCantoSourceUrl(config.devices?.[0]?.option?.groupImageCantoID ?? "") ?? "";
    const quantities = MonitoringProductItem.buildDeviceQuantityMap(config.getOptions());

    let observable: Observable<string>;
    if (quantities.size === 1) {
      const matNr = Array.from(quantities.keys())[0];
      observable = this.monitoringProductService.init().pipe(
        switchMap(() => this.monitoringProductService.loadDeviceFromDB(matNr, MonitoringCategory.UTILITY, undefined)),
        map((device: any) => {
          const cantoId =
            (device && "productImageCantoID" in device ? device.productImageCantoID : undefined) ||
            config.devices?.[0]?.option?.groupImageCantoID ||
            "";
          return ProductImage.getCantoSourceUrl(cantoId) ?? groupUrl;
        }),
        catchError(() => of(groupUrl)),
        shareReplay(1),
      );
    } else {
      observable = of(groupUrl);
    }

    this.imageUrlCache.set(config, observable);
    return observable;
  }
}
